import type { SetupDraftOut, WorkspaceOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../lib/api-errors";
import { CONFIGURE_STEP } from "./setup-config";
import { useSaveProgress } from "./use-onboarding";
import { useTemplateDraft } from "./use-setup-draft";

/**
 * Step-3 template switch: loads that template's draft, remembers the choice in the onboarding
 * progress (so resuming uses it), then hands the draft over to replace the editable fields.
 * `target` is the template being loaded (the editor shows a skeleton meanwhile).
 */
export function useTemplateSwitch(workspace: WorkspaceOut | undefined, onDraft: (draft: SetupDraftOut) => void, onError: (message: string) => void) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const fetchDraft = useTemplateDraft(workspace);
  const save = useSaveProgress(workspace);
  const [target, setTarget] = useState<string | null>(null);

  const run = async (key: string, name: string) => {
    setTarget(key);
    try {
      const draft = await fetchDraft(key);
      await save(CONFIGURE_STEP, { template_key: draft.template_key });
      onDraft(draft);
      toast.success(t("onboarding.configure.switched", { template: name }));
    } catch (caught) {
      onError(message(caught));
    } finally {
      setTarget(null);
    }
  };
  return { target, run };
}
